import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { StickyMobileCTA } from "@/components/StickyMobileCTA";
import AmbientBackground from "@/components/AmbientBackground";
import { TrailingSlashRedirect } from "@/components/TrailingSlashRedirect";
import Home from "./pages/Home";
import Websites from "./pages/Websites";
import GrowthAddOns from "./pages/GrowthAddOns";
import GoogleAds from "./pages/GoogleAds";
import GBPOptimization from "./pages/GBPOptimization";
import LawnCareWebsiteDesign from "./pages/LawnCareWebsiteDesign";
import LawnCareWebsiteExamples from "./pages/LawnCareWebsiteExamples";
import LandscapingWebsiteExamples from "./pages/LandscapingWebsiteExamples";
import ServiceAreaPagesLocalSEO from "./pages/ServiceAreaPagesLocalSEO";
import ServicePagesThatRank from "./pages/ServicePagesThatRank";
import LawnCareWebsiteCost from "./pages/LawnCareWebsiteCost";
import LandscapingWebsiteCost from "./pages/LandscapingWebsiteCost";
import LandscapingWebsiteDesign from "./pages/LandscapingWebsiteDesign";
import LawnCareQuoteForm from "./pages/LawnCareQuoteForm";
import LandscapingQuoteForm from "./pages/LandscapingQuoteForm";
import LawnCareWebsiteContent from "./pages/LawnCareWebsiteContent";
import LandscapingWebsiteContent from "./pages/LandscapingWebsiteContent";
import LawnCareWebsiteSEO from "./pages/LawnCareWebsiteSEO";
import LandscapingWebsiteSEO from "./pages/LandscapingWebsiteSEO";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Resources from "./pages/Resources";
import LawnCareWebsiteChecklist from "./pages/resources/LawnCareWebsiteChecklist";
import HowToGetMoreLawnCareLeads from "./pages/resources/HowToGetMoreLawnCareLeads";
import GoogleBusinessProfileForLawnCare from "./pages/resources/GoogleBusinessProfileForLawnCare";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import LawnCareWebsiteDesignPricing from "./pages/LawnCareWebsiteDesignPricing";
import LandscapingWebsiteDesignPricing from "./pages/LandscapingWebsiteDesignPricing";
import LawnCareWebsiteRedesign from "./pages/LawnCareWebsiteRedesign";
import LandscapingPortfolioWebsite from "./pages/LandscapingPortfolioWebsite";
import LawnCareWebsiteSEOServices from "./pages/LawnCareWebsiteSEOServices";
import LawnCareWebDesignAgency from "./pages/LawnCareWebDesignAgency";
import LandscapingWebDesignAgency from "./pages/LandscapingWebDesignAgency";
import LawnCareWebsiteDesigner from "./pages/LawnCareWebsiteDesigner";
import LandscapingWebsiteDesigner from "./pages/LandscapingWebsiteDesigner";
import LawnCareWebsiteMaintenance from "./pages/LawnCareWebsiteMaintenance";
import LandscapingWebsiteMaintenance from "./pages/LandscapingWebsiteMaintenance";
import LawnCareWebsiteHosting from "./pages/LawnCareWebsiteHosting";
import LandscapingWebsiteHosting from "./pages/LandscapingWebsiteHosting";
import LawnCareWebsiteCopywriting from "./pages/LawnCareWebsiteCopywriting";
import LandscapingWebsiteCopywriting from "./pages/LandscapingWebsiteCopywriting";
import NotFound from "./pages/NotFound";
import ThankYou from "./pages/ThankYou";

// Comparison pages
import ComparisonsHub from "./pages/comparisons/ComparisonsHub";
import WixVsWordpressLawnCare from "./pages/comparisons/WixVsWordpressLawnCare";
import SquarespaceVsWordpressLandscaping from "./pages/comparisons/SquarespaceVsWordpressLandscaping";
import WordpressVsBuilderLawnCare from "./pages/comparisons/WordpressVsBuilderLawnCare";
import BestWebsiteBuilderLawnCare from "./pages/comparisons/BestWebsiteBuilderLawnCare";
import BestWebsiteBuilderLandscapers from "./pages/comparisons/BestWebsiteBuilderLandscapers";
import AgencyVsFreelancer from "./pages/comparisons/AgencyVsFreelancer";
import CheapVsProfessional from "./pages/comparisons/CheapVsProfessional";
import OneTimeVsSubscription from "./pages/comparisons/OneTimeVsSubscription";
import SEOvsAds from "./pages/comparisons/SEOvsAds";
import WebsiteVsWebsitePlusGBP from "./pages/comparisons/WebsiteVsWebsitePlusGBP";
import BestCRMForms from "./pages/comparisons/BestCRMForms";
import CallTrackingTools from "./pages/comparisons/CallTrackingTools";

// Website fixes pages
import WebsiteFixesHub from "./pages/website-fixes/WebsiteFixesHub";
import LawnCareNotGettingLeads from "./pages/website-fixes/LawnCareNotGettingLeads";
import LandscapingNotGettingLeads from "./pages/website-fixes/LandscapingNotGettingLeads";
import LawnCareSlowMobile from "./pages/website-fixes/LawnCareSlowMobile";
import LandscapingWebsiteSlow from "./pages/website-fixes/LandscapingWebsiteSlow";
import QuoteFormNotSubmitting from "./pages/website-fixes/QuoteFormNotSubmitting";
import LandscapingQuoteFormConversion from "./pages/website-fixes/LandscapingQuoteFormConversion";
import ServicePagesNotRanking from "./pages/website-fixes/ServicePagesNotRanking";
import ServiceAreaPagesNotRanking from "./pages/website-fixes/ServiceAreaPagesNotRanking";
import NoCallsFromGoogleMaps from "./pages/website-fixes/NoCallsFromGoogleMaps";
import HighTrafficNoCalls from "./pages/website-fixes/HighTrafficNoCalls";
import StickyCallButton from "./pages/website-fixes/StickyCallButton";
import AddReviewsToWebsite from "./pages/website-fixes/AddReviewsToWebsite";

// Website templates pages
import WebsiteTemplatesHub from "./pages/website-templates/WebsiteTemplatesHub";
import LawnCareHomepageTemplate from "./pages/website-templates/LawnCareHomepageTemplate";
import LandscapingHomepageTemplate from "./pages/website-templates/LandscapingHomepageTemplate";
import LawnCareServicePageTemplate from "./pages/website-templates/LawnCareServicePageTemplate";
import LandscapingServicePageTemplate from "./pages/website-templates/LandscapingServicePageTemplate";
import ServiceAreaPageTemplate from "./pages/website-templates/ServiceAreaPageTemplate";
import LawnCareWebsiteSEOChecklist from "./pages/website-templates/LawnCareWebsiteSEOChecklist";
import LandscapingWebsiteSEOChecklist from "./pages/website-templates/LandscapingWebsiteSEOChecklist";
import LawnCareQuoteFormTemplate from "./pages/website-templates/LawnCareQuoteFormTemplate";

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
<BrowserRouter>
          <TrailingSlashRedirect />
          <AmbientBackground />
          <div className="flex flex-col min-h-screen pb-[60px] md:pb-0 relative z-10">
            <Header />
            <StickyMobileCTA />
            <main className="flex-1">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/websites" element={<Websites />} />
                <Route path="/growth-add-ons" element={<GrowthAddOns />} />
                <Route path="/growth-add-ons/google-ads" element={<GoogleAds />} />
                <Route path="/growth-add-ons/gbp" element={<GBPOptimization />} />
                <Route path="/lawn-care-website-design" element={<LawnCareWebsiteDesign />} />
                <Route path="/lawn-care-website-examples" element={<LawnCareWebsiteExamples />} />
                <Route path="/landscaping-website-examples" element={<LandscapingWebsiteExamples />} />
                <Route path="/service-area-pages-local-seo" element={<ServiceAreaPagesLocalSEO />} />
                <Route path="/service-pages-that-rank" element={<ServicePagesThatRank />} />
                <Route path="/lawn-care-website-cost" element={<LawnCareWebsiteCost />} />
                <Route path="/landscaping-website-cost" element={<LandscapingWebsiteCost />} />
                <Route path="/landscaping-website-design" element={<LandscapingWebsiteDesign />} />
                <Route path="/lawn-care-quote-form" element={<LawnCareQuoteForm />} />
                <Route path="/landscaping-quote-form" element={<LandscapingQuoteForm />} />
                <Route path="/lawn-care-website-content" element={<LawnCareWebsiteContent />} />
                <Route path="/landscaping-website-content" element={<LandscapingWebsiteContent />} />
                <Route path="/lawn-care-website-seo" element={<LawnCareWebsiteSEO />} />
                <Route path="/landscaping-website-seo" element={<LandscapingWebsiteSEO />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/resources" element={<Resources />} />
                <Route path="/resources/lawn-care-website-checklist" element={<LawnCareWebsiteChecklist />} />
                <Route path="/resources/how-to-get-more-lawn-care-leads-online" element={<HowToGetMoreLawnCareLeads />} />
                <Route path="/resources/google-business-profile-for-lawn-care" element={<GoogleBusinessProfileForLawnCare />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/lawn-care-website-design-pricing" element={<LawnCareWebsiteDesignPricing />} />
                <Route path="/landscaping-website-design-pricing" element={<LandscapingWebsiteDesignPricing />} />
                <Route path="/lawn-care-website-redesign" element={<LawnCareWebsiteRedesign />} />
                <Route path="/landscaping-portfolio-website" element={<LandscapingPortfolioWebsite />} />
                <Route path="/lawn-care-website-seo-services" element={<LawnCareWebsiteSEOServices />} />
                <Route path="/lawn-care-web-design-agency" element={<LawnCareWebDesignAgency />} />
                <Route path="/landscaping-web-design-agency" element={<LandscapingWebDesignAgency />} />
                <Route path="/lawn-care-website-designer" element={<LawnCareWebsiteDesigner />} />
                <Route path="/landscaping-website-designer" element={<LandscapingWebsiteDesigner />} />
                <Route path="/lawn-care-website-maintenance" element={<LawnCareWebsiteMaintenance />} />
                <Route path="/landscaping-website-maintenance" element={<LandscapingWebsiteMaintenance />} />
                <Route path="/lawn-care-website-hosting" element={<LawnCareWebsiteHosting />} />
                <Route path="/landscaping-website-hosting" element={<LandscapingWebsiteHosting />} />
                <Route path="/lawn-care-website-copywriting" element={<LawnCareWebsiteCopywriting />} />
                <Route path="/landscaping-website-copywriting" element={<LandscapingWebsiteCopywriting />} />
                
                {/* Comparison pages */}
                <Route path="/comparisons" element={<ComparisonsHub />} />
                <Route path="/comparisons/wix-vs-wordpress-lawn-care" element={<WixVsWordpressLawnCare />} />
                <Route path="/comparisons/squarespace-vs-wordpress-landscaping" element={<SquarespaceVsWordpressLandscaping />} />
                <Route path="/comparisons/wordpress-vs-website-builder-lawn-care" element={<WordpressVsBuilderLawnCare />} />
                <Route path="/comparisons/best-website-builder-lawn-care" element={<BestWebsiteBuilderLawnCare />} />
                <Route path="/comparisons/best-website-builder-landscapers" element={<BestWebsiteBuilderLandscapers />} />
                <Route path="/comparisons/agency-vs-freelancer" element={<AgencyVsFreelancer />} />
                <Route path="/comparisons/cheap-vs-professional-website" element={<CheapVsProfessional />} />
                <Route path="/comparisons/one-time-vs-subscription-website" element={<OneTimeVsSubscription />} />
                <Route path="/comparisons/seo-vs-google-ads" element={<SEOvsAds />} />
                <Route path="/comparisons/website-vs-website-plus-gbp" element={<WebsiteVsWebsitePlusGBP />} />
                <Route path="/comparisons/best-crm-forms-lawn-care" element={<BestCRMForms />} />
                <Route path="/comparisons/call-tracking-tools" element={<CallTrackingTools />} />
                
                {/* Website fixes pages */}
                <Route path="/website-fixes" element={<WebsiteFixesHub />} />
                <Route path="/website-fixes/lawn-care-website-not-getting-leads" element={<LawnCareNotGettingLeads />} />
                <Route path="/website-fixes/landscaping-website-not-getting-leads" element={<LandscapingNotGettingLeads />} />
                <Route path="/website-fixes/lawn-care-website-slow-mobile" element={<LawnCareSlowMobile />} />
                <Route path="/website-fixes/landscaping-website-slow" element={<LandscapingWebsiteSlow />} />
                <Route path="/website-fixes/quote-form-not-submitting" element={<QuoteFormNotSubmitting />} />
                <Route path="/website-fixes/landscaping-quote-form-conversion" element={<LandscapingQuoteFormConversion />} />
                <Route path="/website-fixes/service-pages-not-ranking" element={<ServicePagesNotRanking />} />
                <Route path="/website-fixes/service-area-pages-not-ranking" element={<ServiceAreaPagesNotRanking />} />
                <Route path="/website-fixes/no-calls-from-google-maps" element={<NoCallsFromGoogleMaps />} />
                <Route path="/website-fixes/high-traffic-no-calls" element={<HighTrafficNoCalls />} />
                <Route path="/website-fixes/sticky-call-button" element={<StickyCallButton />} />
                <Route path="/website-fixes/add-reviews-to-website" element={<AddReviewsToWebsite />} />
                
                {/* Website templates pages */}
                <Route path="/website-templates" element={<WebsiteTemplatesHub />} />
                <Route path="/website-templates/lawn-care-homepage-template" element={<LawnCareHomepageTemplate />} />
                <Route path="/website-templates/landscaping-homepage-template" element={<LandscapingHomepageTemplate />} />
                <Route path="/website-templates/lawn-care-service-page-template" element={<LawnCareServicePageTemplate />} />
                <Route path="/website-templates/landscaping-service-page-template" element={<LandscapingServicePageTemplate />} />
                <Route path="/website-templates/service-area-page-template" element={<ServiceAreaPageTemplate />} />
                <Route path="/website-templates/lawn-care-website-seo-checklist" element={<LawnCareWebsiteSEOChecklist />} />
                <Route path="/website-templates/landscaping-website-seo-checklist" element={<LandscapingWebsiteSEOChecklist />} />
                <Route path="/website-templates/lawn-care-quote-form-template" element={<LawnCareQuoteFormTemplate />} />
                
                {/* Thank you page (noindex) */}
                <Route path="/thank-you" element={<ThankYou />} />
                
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
            <Footer />
          </div>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
